/**
 * Decides what to render before the app boots.
 *
 * Three outcomes:
 *   embedded          → Shopify authenticated us; render immediately.
 *   standalone, known → resolve the store id from /account/me, then render.
 *   standalone, new   → show the sign-in screen.
 *
 * The `/account/me` call is not optional politeness. In standalone mode the
 * store id lives on the server, not in the bundle — every API path interpolates
 * it, so rendering the app before it resolves produces a burst of requests
 * against the placeholder id, each of which correctly 403s. Blocking on one
 * request avoids that entirely.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, auth, isEmbedded, setStoreId, type Account } from '../api';
import SignIn from '../pages/SignIn';

type State =
  | { phase: 'checking' }
  | { phase: 'authed'; account: Account | null }
  | { phase: 'anonymous' }
  | { phase: 'error'; message: string };

export default function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<State>({ phase: 'checking' });

  const resolve = useCallback(() => {
    // Embedded: App Bridge supplies a fresh token, but route paths still need
    // our internal store id. Resolve it before rendering so the first screen
    // never fires requests using the development placeholder id.
    if (isEmbedded()) {
      api
        .stores()
        .then(([store]) => {
          if (!store) throw new Error('Store not installed');
          setStoreId(store.id);
          setState({ phase: 'authed', account: null });
        })
        .catch((err) => {
          setState({
            phase: 'error',
            message: err instanceof Error ? err.message : 'Could not reach the server.',
          });
        });
      return;
    }
    if (!auth.isSignedIn) {
      setState({ phase: 'anonymous' });
      return;
    }
    api
      .me()
      .then((res) => {
        setStoreId(res.storeId);
        setState({ phase: 'authed', account: res.account });
      })
      .catch((err) => {
        // 401 already cleared the dead token inside the client, so falling back
        // to the sign-in screen is the correct response rather than an error.
        if (!auth.isSignedIn) setState({ phase: 'anonymous' });
        else
          setState({
            phase: 'error',
            message: err instanceof Error ? err.message : 'Could not reach the server.',
          });
      });
  }, []);

  useEffect(resolve, [resolve]);

  if (state.phase === 'checking') {
    return (
      <div className="auth-shell">
        <div className="auth-loading">Loading your account…</div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1>Can't reach ScaleAds AI</h1>
          <p className="auth-sub">{state.message}</p>
          <p className="auth-sub">
            Your campaigns are unaffected — they run on the ad platforms, not here.
          </p>
          <button className="btn brand auth-submit" onClick={() => window.location.reload()}>
            Try again
          </button>
          <p className="auth-switch">
            <button
              type="button"
              onClick={() => {
                auth.clear();
                setState({ phase: 'anonymous' });
              }}
            >
              Sign out
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (state.phase === 'anonymous') {
    return <SignIn onAuthed={(account) => setState({ phase: 'authed', account })} />;
  }

  return <>{children}</>;
}
